import { http } from '@/lib/http';

const BASE = '/api/v1/reports';

export type SalesPeriodGrouping = 'DAY' | 'WEEK' | 'MONTH';

export interface SalesByPeriodRow {
  bucket: string;
  status: string;
  /** ISO 4217 currency for this row — sales are grouped by currency so
   *  multi-currency tenants never see amounts summed across currencies. */
  currency: string;
  orderCount: number;
  totalAmount: number;
}

export interface SalesByPeriodResult {
  groupBy: SalesPeriodGrouping;
  from: string;
  to: string;
  rows: SalesByPeriodRow[];
}

export interface StockValuationRow {
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  itemId: string;
  sku: string;
  itemName: string;
  quantityOnHand: number;
  costPrice: number | null;
  valuation: number;
}

export interface StockValuationResult {
  rows: StockValuationRow[];
  totalsPerWarehouse: Array<{
    warehouseId: string;
    warehouseCode: string;
    warehouseName: string;
    valuation: number;
  }>;
  grandTotal: number;
  unpricedItemCount: number;
}

export interface RefundsSummaryRow {
  month: string;
  /** ISO 4217 — captured/refunded grouped by currency (never summed across). */
  currency: string;
  capturedAmount: number;
  refundedAmount: number;
  netAmount: number;
}

export interface RefundsSummaryResult {
  from: string;
  to: string;
  rows: RefundsSummaryRow[];
  /** Per-currency totals. */
  totals: Array<{ currency: string; captured: number; refunded: number; net: number }>;
}

export const reportsService = {
  salesByPeriod: (query: { from?: string; to?: string; groupBy?: SalesPeriodGrouping } = {}) =>
    http.get<SalesByPeriodResult>(`${BASE}/sales`, { params: query }),
  stockValuation: (query: { warehouseId?: string } = {}) =>
    http.get<StockValuationResult>(`${BASE}/stock-valuation`, { params: query }),
  refundsSummary: (query: { from?: string; to?: string } = {}) =>
    http.get<RefundsSummaryResult>(`${BASE}/refunds`, { params: query }),

  /** Build a CSV download URL for the active session — operator hits this from a real anchor. */
  csvUrl: (
    report: 'sales' | 'stock-valuation' | 'refunds',
    query: Record<string, string | undefined> = {},
  ) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) params.append(k, v);
    const qs = params.toString();
    return `${BASE}/${report}.csv${qs ? `?${qs}` : ''}`;
  },
};
